/**
 * @description Componente de cadastro de paciente com múltiplas etapas
 * @module SignupPatient
 * @since Dezembro 2024
 */

import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  TextField,
  MenuItem,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Switch,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import logo from "../../assets/fluxmed-logo-horizontal.png";
import background from "../../assets/background.png";

import {
  PersonOutlined as PersonIcon,
  CheckCircleOutlined as CheckIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Login as LoginIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Wc as GenderIcon,
  Badge as BadgeIcon,
  WhatsApp as WhatsAppIcon,
  Description as DescriptionIcon,
  Healing as HealingIcon,
  Pets as PetsIcon,
  AssignmentInd as AssignmentIndIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useFormatter } from "@/utils/useFormatter";
import { useRegex } from "@/utils/useRegex";
import { useValidate } from "@/utils/useValidate";
import { useFirebaseService } from "@/utils/useFirebase";
import { toast } from "react-toastify";
import { SearchCPF } from "@/services/scim/signup/SearchCPF";
import { SignUpRegister } from "@/services/scim/signup/SignUpRegister";
import { HTTPResponseStatus } from "@/utils/enum";
import { FormInputValidationNumber } from "./components/forminputinvalidationnumber";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import TermText from "@/pages/TermsOfUse/components/TermText";
import { ProfileSearch } from "@/services/scim";
import { PutPerson } from "@/services/fhir/person/PutPerson";
import { currentFeatures, endpoints } from "@/configs/Settings";
import { OAuthUser } from "@/services/auth";
import { GetPatient } from "@/services/fhir";
import {
  setRefreshToken,
  setUserAccessToken,
  setUserAccessTokenExpiresAt,
} from "src-next/shared/api/utils/tokenStorage";

// Estilos CSS para animação de pontos de carregamento
const loadingDotsStyle = `
  @keyframes loadingDots {
    0% { content: '.'; }
    33% { content: '..'; }
    66% { content: '...'; }
    100% { content: '.'; }
  }
  .loading-dots::after {
    content: '.';
    animation: loadingDots 1.5s infinite;
    display: inline-block;
    width: 20px;
    text-align: left;
  }
`;

// Enum para controlar as etapas do cadastro
enum SignupSteps {
  PERSONAL_DATA = 0,
  TOKEN_VERIFICATION = 1,
  PASSWORD_CREATION = 2,
  TERMS_OF_USE = 3,
  SUCCESS = 4,
}

// Interface para os dados do CPF validado
interface ICPFValidationData {
  ni: string;
  nome: string;
  situacao: {
    codigo: string;
    descricao: string;
  };
  nascimento: string;
}

// Interface para os dados do formulário
interface IPatientFormData {
  cpf: string;
  phoneNumber: string;
  email: string;
  instagram: string;
  gender: string;
  confirmationToken: string;
  password: string;
  confirmPassword: string;
  notification: boolean;
  termsAccepted: boolean;
  isPractitioner: boolean; // Indica se o usuário é profissional de saúde (médico ou veterinário)
}

// Definição das etapas visuais do Stepper (simplificado para 2 etapas)
// As etapas intermediárias (verificação de telefone e senha) são tratadas internamente
const steps = [
  {
    label: "Cadastro",
    icon: PersonIcon,
    description: "Informações do paciente",
  },
  {
    label: "Termos de Uso",
    icon: DescriptionIcon,
    description: "Aceite dos termos",
  },
];

/**
 * Retorna o índice visual do Stepper baseado na etapa atual do cadastro
 * Etapas PERSONAL_DATA e PASSWORD_CREATION = step 0 (Cadastro)
 * Etapa TERMS_OF_USE = step 1 (Termos de Uso)
 */
const getVisualStepIndex = (currentStep: SignupSteps): number => {
  if (currentStep === SignupSteps.TERMS_OF_USE) {
    return 1; // Termos de Uso
  }
  return 0; // Cadastro (inclui dados pessoais, verificação e senha)
};

const SignupPatient: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Controle da etapa atual
  const [activeStep, setActiveStep] = useState<SignupSteps>(
    SignupSteps.PERSONAL_DATA,
  );

  // Estado para controlar envio do token e timer
  const [tokenSent, setTokenSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openTokenDialog, setOpenTokenDialog] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>(
    {},
  );

  // Estado para armazenar os dados do CPF validado
  const [cpfValidationData, setCpfValidationData] =
    useState<ICPFValidationData | null>(null);

  // Configuração do react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<IPatientFormData>({
    mode: "onSubmit",
    defaultValues: {
      cpf: "",
      phoneNumber: "",
      email: "",
      instagram: "",
      gender: "",
      confirmationToken: "",
      password: "",
      confirmPassword: "",
      notification: true,
      termsAccepted: false,
      isPractitioner: false,
    },
  });

  // Observar valores do formulário
  const watchedCpf = watch("cpf");
  const watchedPhone = watch("phoneNumber");
  const watchedEmail = watch("email");
  const watchedGender = watch("gender");
  const watchedPassword = watch("password");
  const watchedTermsAccepted = watch("termsAccepted");
  const watchedIsPractitioner = watch("isPractitioner");

  // Função auxiliar para transformar data de nascimento
  function transformData(inputData: string) {
    const day = inputData.substring(0, 2);
    const month = inputData.substring(2, 4);
    const year = inputData.substring(4, 8);
    const formatedDate = [year, month, day]?.join("-");
    return formatedDate;
  }

  /**
   * @description Formata a data de nascimento do formato DDMMYYYY para DD/MM/YYYY
   */
  const formatBirthDate = (dateString: string): string => {
    if (!dateString || dateString.length !== 8) return dateString;
    const day = dateString.substring(0, 2);
    const month = dateString.substring(2, 4);
    const year = dateString.substring(4, 8);
    return `${day}/${month}/${year}`;
  };

  /**
   * @description Formata o sexo de nascimento para exibição em português
   */
  const formatGender = (gender: string): string => {
    if (gender === "male") return "Masculino";
    if (gender === "female") return "Feminino";
    return gender;
  };

  // Timer para habilitar reenvio do código
  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    if (openTokenDialog && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            toast.info("Você já pode solicitar um novo código de verificação.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [openTokenDialog, timer]);

  /**
   * Valida o CPF do usuário antes de prosseguir
   * Verifica se o CPF já está cadastrado no SCIM
   * Se não estiver no SCIM, verifica se existe como Patient no FHIR
   * Se não existir no FHIR, valida no Serpro
   */
  const validateUserCPF = async (cpf: string) => {
    try {
      const cleanCPF = cpf.replace(/[^0-9]/g, "");

      // Verificar se o CPF já está cadastrado no SCIM
      const Scim2UserResponse = await ProfileSearch(
        "application",
        cleanCPF,
      ).then((response) => response?.Resources);

      // Se o usuário existe no SCIM, não pode cadastrar novamente
      if (Scim2UserResponse && Scim2UserResponse.length > 0) {
        toast.warn("CPF já cadastrado no sistema. Tente fazer login.");
        return false;
      }

      // Se não existe no SCIM, verificar se existe como Patient no FHIR
      console.log("CPF não encontrado no SCIM, verificando no FHIR...");
      try {
        const patientResponse = await GetPatient({
          authType: "application",
          identifier: cleanCPF,
        });

        // Se encontrou o paciente no FHIR, usar os dados dele
        if (patientResponse && patientResponse.data) {
          console.log("Paciente encontrado no FHIR:", patientResponse.data);

          // Extrair dados do paciente FHIR
          const patient = patientResponse.data;
          const patientName = patient.name?.[0]?.text || "";
          const patientBirthDate = patient.birthDate || "";

          // Formatar data de nascimento do formato YYYY-MM-DD para DDMMYYYY
          let formattedBirthDate = "";
          if (patientBirthDate) {
            const [year, month, day] = patientBirthDate.split("-");
            formattedBirthDate = `${day}${month}${year}`;
          }

          // Criar objeto no formato esperado (similar ao Serpro)
          const validationData: ICPFValidationData = {
            ni: cleanCPF,
            nome: patientName,
            situacao: {
              codigo: "0", // Código genérico para paciente já validado
              descricao: "Regular", // Status genérico
            },
            nascimento: formattedBirthDate,
          };

          setCpfValidationData(validationData);
          console.log(
            "Dados do paciente FHIR carregados com sucesso para registro",
          );
          return true;
        }
      } catch (fhirError) {
        // Se não encontrou no FHIR, continuar para validação no Serpro
        console.log(
          "Paciente não encontrado no FHIR, validando no Serpro...",
          fhirError,
        );
      }

      // Se não existe no FHIR, validar CPF no Serpro
      console.log("Validando CPF no Serpro...");
      try {
        const serproResponse = await SearchCPF(cleanCPF, "application");
        console.log("Resposta da validação de CPF:", serproResponse);

        // Se o CPF estiver em uso no SCIM (status 422)
        if (
          serproResponse &&
          serproResponse.status === HTTPResponseStatus.UnprocessableEntity
        ) {
          toast.warn(
            "Não foi possível validar o CPF ou é menor de idade. Por favor, tente novamente.",
          );
          return false; // Não continuar com o envio de código
        }

        // CPF válido mas não cadastrado, armazenar dados para registro posterior
        if (serproResponse && serproResponse.data) {
          // Armazenar os dados do CPF validado
          const validationData = serproResponse.data as ICPFValidationData;
          setCpfValidationData(validationData);
          console.log(
            "CPF validado com sucesso no Serpro, dados obtidos para registro posterior",
          );
        }

        // CPF validado, pode prosseguir
        return true;
      } catch (serproError) {
        console.error("Erro ao validar CPF no Serpro:", serproError);
        toast.error("Erro ao validar CPF. Verifique se o CPF é válido.");
        return false;
      }
    } catch (error) {
      console.error("Erro ao validar CPF:", error);
      toast.error("Erro ao validar CPF. Tente novamente.");
      return false;
    }
  };

  /**
   * Envia código de confirmação para o telefone
   */
  const handleSendToken = async () => {
    // Verificar campos obrigatórios antes de enviar
    const requiredFields = {
      cpf: watchedCpf && useValidate.cpfNumber(watchedCpf),
      phoneNumber: watchedPhone && useRegex.phoneNumber.test(watchedPhone),
      email: watchedEmail && useRegex.email.test(watchedEmail),
      gender: !!watchedGender,
    };

    // Verificar se há campos inválidos
    const hasErrors = Object.entries(requiredFields).some(
      ([_, isValid]) => !isValid,
    );
    setFieldErrors(
      Object.fromEntries(
        Object.entries(requiredFields).map(([key, isValid]) => [key, !isValid]),
      ),
    );

    if (hasErrors) {
      toast.error("Preencha todos os campos obrigatórios corretamente");
      return;
    }

    setLoading(true);
    try {
      // Validar CPF antes de prosseguir
      const canProceed = await validateUserCPF(watchedCpf);
      if (!canProceed) {
        setLoading(false);
        return;
      }

      // Limpar token anterior se existir
      reset((formValues) => ({
        ...formValues,
        confirmationToken: "",
      }));

      // Integração com Firebase para enviar token apenas para o telefone
      await useFirebaseService.sendVerificationCode(watchedPhone);
      toast.success(`Código enviado para ${watchedPhone}`);
      setTokenSent(true);
      setOpenTokenDialog(true);
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      console.error("Erro ao enviar token:", error);
      toast.error("Erro ao enviar código de verificação");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reenvia o código de confirmação
   */
  const handleResendToken = async () => {
    setLoading(true);
    try {
      // Integração com Firebase para reenviar token
      await useFirebaseService.sendVerificationCode(watchedPhone);
      toast.info("Código reenviado com sucesso!");
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      console.error("Erro ao reenviar token:", error);
      toast.error("Erro ao reenviar código de verificação");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Prossegue sem verificar o token
   */
  const handleSkipToken = () => {
    toast.info(
      "Verificação ignorada. Você poderá validar seu número mais tarde.",
    );
    setOpenTokenDialog(false);
    setActiveStep(SignupSteps.PASSWORD_CREATION);
  };

  /**
   * Valida e confirma o token
   */
  const handleVerifyToken = async () => {
    const token = watch("confirmationToken");
    if (token && token.length === 6) {
      setLoading(true);
      try {
        // Integração com Firebase para verificar token
        await useFirebaseService.verifyCode(token);
        toast.success("Código validado com sucesso!");
        setOpenTokenDialog(false);
        setActiveStep(SignupSteps.PASSWORD_CREATION);
      } catch (error) {
        console.error("Erro ao verificar token:", error);
        toast.error("Código inválido. Tente novamente.");
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn("Digite o código completo de 6 dígitos");
    }
  };

  /**
   * Registra o usuário no sistema usando a API
   */
  const registerUser = async (data: IPatientFormData) => {
    try {
      // Preparar os dados para registro
      const registerData = {
        firstName: cpfValidationData?.nome.split(" ")[0] || "",
        surname: cpfValidationData?.nome.split(" ").slice(1).join(" ") || "",
        identifier: data.cpf.replace(/[^0-9]/g, ""),
        birthdate: dayjs(cpfValidationData?.nascimento, "DDMMYYYY"),
        instagram: data.instagram,
        password: data.password,
        passwordConfirm: data.confirmPassword,
        phoneNumber: `+55${data.phoneNumber}`,
        email: data.email,
        gender: data.gender,
        firebaseCode: data.confirmationToken || "",
        firebaseToken: "123456",
        notification: data.notification,
      };

      // Chamar a API para registrar o usuário
      const response = await SignUpRegister(registerData);
      const dataPerson = {
        identifier: data.cpf.replace(/[^0-9]/g, ""),
        name: cpfValidationData?.nome,
        birthDate: dayjs(cpfValidationData?.nascimento, "DDMMYYYY"),
        active: true,
      };
      await PutPerson(dataPerson, true);

      if (
        response &&
        (response.status === HTTPResponseStatus.Created ||
          response.status === HTTPResponseStatus.Conflict)
      ) {
        if (response.status === HTTPResponseStatus.Created) {
          toast.success("Usuário cadastrado com sucesso!");
        } else {
          toast.warn("Usuário já existe no sistema.");
        }

        return true; // Continuar em ambos os casos
      } else {
        toast.error("Erro ao cadastrar usuário. Tente novamente.");
        return false;
      }
    } catch (error) {
      console.error("Erro ao registrar usuário:", error);
      toast.error("Erro ao cadastrar usuário. Tente novamente.");
      return false;
    }
  };

  /**
   * Valida e finaliza o cadastro com senha
   * Apenas avança para os termos de uso (registro feito após aceitar termos)
   */
  const handleConfirmPassword = async () => {
    // Avança para a próxima etapa (termos de uso)
    setActiveStep(SignupSteps.TERMS_OF_USE);
  };

  /**
   * Finaliza o cadastro após aceitar os termos
   * Realiza o registro do usuário apenas após aceitar os termos de uso
   * Após o cadastro bem-sucedido, realiza login automático
   */
  const handleCompleteRegistration = async (data: IPatientFormData) => {
    if (!watchedTermsAccepted) {
      toast.error("Você precisa aceitar os Termos de Uso para continuar.");
      return;
    }

    setLoading(true);
    try {
      // Registrar o usuário após aceitar os termos
      // No signup, sempre precisamos registrar o usuário quando temos dados do CPF validado
      if (cpfValidationData) {
        const registered = await registerUser(data);
        if (!registered) {
          throw new Error("Falha ao registrar usuário");
        }
      }

      // Cadastro concluído com sucesso
      toast.success("Cadastro realizado com sucesso!");

      // Realizar login automático após cadastro bem-sucedido
      // Limpar o CPF removendo caracteres não numéricos
      const cleanCPF = data.cpf.replace(/\D/g, "");

      try {
        // Autenticar o usuário usando as credenciais cadastradas
        const authResponse = await OAuthUser(cleanCPF, data.password);

        if (authResponse) {
          // Tokens via tokenStorage; demais flags permanecem no localStorage
          setUserAccessToken(authResponse.access_token);
          localStorage.setItem("logged", "true");
          localStorage.setItem("username", cleanCPF);
          setRefreshToken(authResponse.refresh_token);
          localStorage.setItem("access", JSON.stringify({ type: "patient" }));

          setUserAccessTokenExpiresAt(
            Date.now() + authResponse.expires_in * 1000,
          );

          // Login automático bem-sucedido
          toast.success("Login realizado automaticamente!");

          // Verificar se o usuário é profissional de saúde (médico/veterinário)
          if (data.isPractitioner) {
            // Redirecionar para registro profissional em nova aba
            toast.info(
              currentFeatures.defaultApp
                ? "Redirecionando para completar seu registro médico..."
                : "Redirecionando para completar seu registro veterinário...",
            );
            window.open(`${endpoints.XDSB_domain}/register/#/`);
            // Também redirecionar a aba atual para a página inicial
            navigate("/");
          } else {
            // Usuário comum, redirecionar apenas para página inicial
            navigate("/");
          }
        } else {
          // Se o login automático falhar, mostrar tela de sucesso
          // O usuário poderá fazer login manualmente
          console.warn("Login automático falhou, mostrando tela de sucesso");
          setActiveStep(SignupSteps.SUCCESS);
        }
      } catch (loginError) {
        // Se houver erro no login automático, mostrar tela de sucesso
        // O usuário poderá fazer login manualmente
        console.error("Erro ao fazer login automático:", loginError);
        toast.info(
          "Cadastro realizado! Por favor, faça login com suas credenciais.",
        );
        setActiveStep(SignupSteps.SUCCESS);
      }
    } catch (error) {
      console.error("Erro ao finalizar cadastro:", error);
      toast.error("Erro ao finalizar cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Redireciona para a página de login
   */
  const handleGoToLogin = () => {
    navigate("/signin");
  };

  /**
   * Reinicia o processo de cadastro
   */
  const handleRestartProcess = () => {
    // Resetar todos os estados
    setActiveStep(SignupSteps.PERSONAL_DATA);
    setTokenSent(false);
    setCpfValidationData(null);
    setFieldErrors({});
    setTimer(30);
    setCanResend(false);
    setOpenTokenDialog(false);
    setShowPassword(false);
    setShowConfirmPassword(false);

    // Resetar formulário com valores iniciais vazios
    reset({
      cpf: "",
      phoneNumber: "",
      email: "",
      instagram: "",
      gender: "",
      confirmationToken: "",
      password: "",
      confirmPassword: "",
      notification: true,
      termsAccepted: false,
      isPractitioner: false,
    });
  };

  /**
   * Retorna para o passo anterior
   */
  const handleBack = () => {
    if (activeStep === SignupSteps.PASSWORD_CREATION) {
      // Voltar para dados pessoais
      setActiveStep(SignupSteps.PERSONAL_DATA);
      setTokenSent(false);
      // Resetar campos de senha
      reset((formValues) => ({
        ...formValues,
        password: "",
        confirmPassword: "",
        confirmationToken: "",
      }));
      // Resetar visualização de senha
      setShowPassword(false);
      setShowConfirmPassword(false);
      // Fechar dialog de token se estiver aberto
      setOpenTokenDialog(false);
    } else if (activeStep === SignupSteps.TERMS_OF_USE) {
      // Voltar para criação de senha
      setActiveStep(SignupSteps.PASSWORD_CREATION);
    }
  };

  /**
   * Verifica se os dados do passo atual são válidos
   */
  const isStepValid = () => {
    if (activeStep === SignupSteps.PERSONAL_DATA) {
      // Verificar todos os campos obrigatórios
      const isCpfValid = watchedCpf && useValidate.cpfNumber(watchedCpf);
      const isPhoneValid =
        watchedPhone && useRegex.phoneNumber.test(watchedPhone);
      const isEmailValid = watchedEmail && useRegex.email.test(watchedEmail);
      const isGenderValid = !!watchedGender;

      // Atualizar estado de erros para feedback visual
      setFieldErrors({
        cpf: !isCpfValid,
        phoneNumber: !isPhoneValid,
        email: !isEmailValid,
        gender: !isGenderValid,
      });

      return isCpfValid && isPhoneValid && isEmailValid && isGenderValid;
    } else if (activeStep === SignupSteps.PASSWORD_CREATION) {
      const password = watch("password");
      const confirmPassword = watch("confirmPassword");

      return (
        password &&
        password.length >= 5 &&
        confirmPassword &&
        password === confirmPassword
      );
    } else if (activeStep === SignupSteps.TERMS_OF_USE) {
      return watchedTermsAccepted;
    }
    return true;
  };

  /**
   * Renderiza o ícone customizado para cada etapa do Stepper
   */
  const StepIconComponent = (props: {
    active?: boolean;
    completed?: boolean;
    icon: number;
  }) => {
    const { active, completed, icon } = props;
    const StepIcon = steps[icon - 1].icon;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: completed ? "#00A884" : active ? "#00A884" : "grey.300",
          color: completed || active ? "white" : "grey.600",
          transition: "all 0.3s ease",
        }}
      >
        {completed ? (
          <CheckIcon fontSize="small" />
        ) : (
          <StepIcon fontSize="small" />
        )}
      </Box>
    );
  };

  /**
   * Renderiza o formulário de dados pessoais
   */
  const renderPersonalData = () => (
    <Box sx={{ py: isMobile ? 1 : 2 }} key="personal-data">
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Linha 1: CPF e Sexo de Nascimento */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="cpf"
            control={control}
            rules={{
              required: "CPF é obrigatório",
              pattern: {
                value: useRegex.cpf,
                message: "CPF inválido",
              },
              validate: (value) =>
                useValidate.cpfNumber(value) || "CPF inválido",
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                label="CPF"
                value={value}
                onChange={(e) =>
                  onChange(useFormatter.formatCPF(e.target.value))
                }
                error={!!errors.cpf || fieldErrors.cpf}
                helperText={errors.cpf?.message}
                placeholder="000.000.000-00"
                inputProps={{ maxLength: 14 }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="gender"
            control={control}
            rules={{
              required: "Sexo de nascimento é obrigatório",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="Sexo de Nascimento"
                error={!!errors.gender || fieldErrors.gender}
                helperText={errors.gender?.message}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GenderIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="male">Masculino</MenuItem>
                <MenuItem value="female">Feminino</MenuItem>
              </TextField>
            )}
          />
        </Grid>

        {/* Linha 2: Telefone e Email */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: "Telefone é obrigatório",
              pattern: {
                value: useRegex.phoneNumber,
                message: "Telefone inválido",
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                label="Telefone Celular"
                value={value}
                onChange={(e) =>
                  onChange(useFormatter.formatPhoneNumber(e.target.value))
                }
                error={!!errors.phoneNumber || fieldErrors.phoneNumber}
                helperText={errors.phoneNumber?.message}
                placeholder="(00) 00000-0000"
                inputProps={{ maxLength: 15 }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "E-mail é obrigatório",
              pattern: {
                value: useRegex.email,
                message: "E-mail inválido",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="E-mail"
                error={!!errors.email || fieldErrors.email}
                helperText={errors.email?.message}
                placeholder="exemplo@email.com"
                type="email"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Linha 3: Instagram */}
        <Grid item xs={12}>
          <Controller
            name="instagram"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Instagram (opcional)"
                error={!!errors.instagram}
                helperText={errors.instagram?.message}
                placeholder="seuinstagram"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <InstagramIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Linha 4: Notificações WhatsApp - Card destacado */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "2px solid",
              borderColor: "#00A884",
              bgcolor: "rgba(0, 168, 132, 0.05)",
              borderRadius: 2,
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#008c6e",
                bgcolor: "rgba(0, 168, 132, 0.08)",
                boxShadow: "0 2px 8px rgba(0, 168, 132, 0.15)",
              },
            }}
          >
            <Controller
              name="notification"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: "#25D366",
                        color: "white",
                      }}
                    >
                      <WhatsAppIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#1a1a1a" }}
                      >
                        Notificações por WhatsApp
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666666", lineHeight: 1.3 }}
                      >
                        Receba atualizações importantes
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase": {
                        color: "#ffffff",
                        "&.Mui-checked": {
                          color: "#ffffff",
                          "& + .MuiSwitch-track": {
                            backgroundColor: "#008c6e",
                            opacity: 1,
                          },
                        },
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: "#bdbdbd",
                        opacity: 1,
                      },
                      "& .MuiSwitch-thumb": {
                        boxShadow:
                          "0 0 0 2px rgba(0, 140, 110, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                        boxShadow:
                          "0 0 0 2px rgba(0, 140, 110, 0.5), 0 2px 4px rgba(0, 0, 0, 0.3)",
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Paper>
        </Grid>

        {/* Linha 5: Opção de profissional de saúde */}

        {!currentFeatures.register && !isMobile && (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Você é um profissional da área de saúde?
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Controller
                  name="isPractitioner"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {/* Ícone condicional: médico ou veterinário */}
                          {currentFeatures.defaultApp ? (
                            <HealingIcon color="primary" />
                          ) : (
                            <PetsIcon color="primary" />
                          )}
                          <Typography variant="body2">
                            {currentFeatures.defaultApp
                              ? "Sou médico(a)"
                              : "Sou médico(a) veterinário(a)"}
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  /**
   * Renderiza o dialog de verificação de token
   */
  const renderTokenDialog = () => (
    <Dialog
      open={openTokenDialog}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          setOpenTokenDialog(false);
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Verificação de Telefone</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Alert
            severity="info"
            icon={false}
            sx={{
              mb: 2,
              bgcolor: "#e8f4fd",
              border: "1px solid #bce0fd",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Digite o código de 6 dígitos enviado para:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight="medium"
                sx={{ color: "#0288d1" }}
              >
                {watchedPhone}
              </Typography>
            </Box>
          </Alert>

          {/* Campo Token */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: "center" }}
            >
              Insira o código de 6 dígitos recebido via SMS
            </Typography>

            <FormInputValidationNumber
              label=""
              name="confirmationToken"
              control={control}
              required
              pattern={/^[0-9]{6}$/}
              autoFocus
            />
          </Box>
          {!!errors.confirmationToken && (
            <Typography
              color="error"
              variant="caption"
              sx={{ textAlign: "center", display: "block", mb: 2 }}
            >
              {errors.confirmationToken?.message}
            </Typography>
          )}

          {/* Espaço para separação */}
          <Box sx={{ height: 16 }} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyToken}
            disabled={
              !watch("confirmationToken") ||
              watch("confirmationToken").length < 6 ||
              loading
            }
            sx={{
              bgcolor: "#00A884",
              "&:hover": { bgcolor: "#008c6e" },
            }}
          >
            {loading ? (
              <>
                <span style={{ marginRight: "8px" }}>Verificando</span>
                <span className="loading-dots"></span>
              </>
            ) : (
              "Verificar Código"
            )}
          </Button>

          <Stack direction="column" spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResendToken}
              disabled={!canResend || loading}
              sx={{
                borderColor: "#00A884",
                color: "#00A884",
                "&:hover": {
                  borderColor: "#008c6e",
                  bgcolor: "rgba(0, 168, 132, 0.04)",
                },
              }}
            >
              {loading
                ? "Enviando..."
                : canResend
                  ? "Reenviar código"
                  : `Aguarde ${timer}s para reenviar`}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setOpenTokenDialog(false);
                // Resetar o campo de token ao voltar
                reset((formValues) => ({
                  ...formValues,
                  confirmationToken: "",
                }));
                // Voltar para edição dos dados
                setActiveStep(SignupSteps.PERSONAL_DATA);
              }}
              sx={{
                borderColor: "#f0ad4e",
                color: "#f0ad4e",
                "&:hover": {
                  borderColor: "#ec971f",
                  bgcolor: "rgba(240, 173, 78, 0.04)",
                },
              }}
            >
              Revisar dados de telefone
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );

  /**
   * Renderiza o formulário de criação de senha
   */
  const renderPasswordCreation = () => (
    <Box sx={{ py: isMobile ? 1 : 2 }} key="password-creation">
      <Box sx={{ textAlign: "center", mb: isMobile ? 2 : 3 }}>
        <CheckIcon
          sx={{ fontSize: isMobile ? 48 : 60, color: "#00A884", mb: 1 }}
        />
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          Criar Senha de Acesso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Defina uma senha segura para acessar a plataforma
        </Typography>
      </Box>

      {/* Exibir dados validados do CPF quando disponíveis */}
      {cpfValidationData && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(0, 168, 132, 0.05)",
            border: "1px solid",
            borderColor: "rgba(0, 168, 132, 0.2)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Nome */}
            <Grid
              item
              xs={12}
              sm={
                cpfValidationData.nascimento && watchedGender
                  ? 6
                  : cpfValidationData.nascimento || watchedGender
                    ? 8
                    : 12
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon sx={{ color: "#00A884", fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Nome Completo
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {cpfValidationData.nome || ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Data de Nascimento */}
            {cpfValidationData.nascimento && (
              <Grid item xs={12} sm={watchedGender ? 3 : 4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarIcon sx={{ color: "#00A884", fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Data de Nascimento
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatBirthDate(cpfValidationData.nascimento)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Sexo de Nascimento */}
            {watchedGender && (
              <Grid item xs={12} sm={cpfValidationData.nascimento ? 3 : 4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <GenderIcon sx={{ color: "#00A884", fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sexo de Nascimento
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {formatGender(watchedGender)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Campo Senha */}
        <Grid item xs={12}>
          <Controller
            name="password"
            control={control}
            rules={{
              required: "Senha é obrigatória",
              minLength: {
                value: 5,
                message: "A senha deve ter no mínimo 5 caracteres",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type={showPassword ? "text" : "password"}
                label="Senha"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Campo Confirmar Senha */}
        <Grid item xs={12}>
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Confirmação de senha é obrigatória",
              validate: (value) =>
                value === watchedPassword || "As senhas não coincidem",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                label="Confirmar Senha"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Dicas de senha */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              <strong>A senha deve conter:</strong>
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Typography
                variant="caption"
                component="li"
                color="text.secondary"
              >
                Mínimo de 5 caracteres
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  /**
   * Renderiza a tela de termos de uso
   */
  const renderTermsOfUse = () => (
    <Box sx={{ py: isMobile ? 1 : 2 }} key="terms-of-use">
      <Box sx={{ textAlign: "center", mb: isMobile ? 2 : 3 }}>
        <DescriptionIcon
          sx={{ fontSize: isMobile ? 48 : 60, color: "#00A884", mb: 1 }}
        />
        <Typography
          variant={isMobile ? "subtitle1" : "h6"}
          gutterBottom
          sx={{ fontWeight: 500 }}
        >
          Termos de Uso e Política de Privacidade
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Leia atentamente os termos antes de continuar
        </Typography>
      </Box>

      {/* Container com scroll para os termos */}
      <Box
        sx={{
          maxHeight: isMobile ? "280px" : "400px",
          overflowY: "auto",
          border: "1px solid",
          borderColor: "grey.300",
          borderRadius: 2,
          p: isMobile ? 1.5 : 2,
          mb: isMobile ? 2 : 3,
          bgcolor: "grey.50",
          // Estilo de scroll mais suave em mobile
          WebkitOverflowScrolling: "touch",
        }}
      >
        <TermText termOf="patient" />
      </Box>

      {/* Checkbox de aceite */}
      <Controller
        name="termsAccepted"
        control={control}
        rules={{
          required: "Você deve aceitar os termos para continuar",
        }}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant={isMobile ? "caption" : "body2"}>
                Eu li e estou de acordo com os Termos de Uso e Política de
                Privacidade
              </Typography>
            }
          />
        )}
      />

      {/* Mensagem de aviso se não aceitar */}
      {!watchedTermsAccepted && (
        <Alert severity="warning" sx={{ mt: isMobile ? 1.5 : 2 }}>
          <Typography variant={isMobile ? "caption" : "body2"}>
            Aceite os Termos de Uso e Política de Privacidade para continuar o
            acesso à plataforma e seus serviços.
          </Typography>
        </Alert>
      )}
    </Box>
  );

  /**
   * Renderiza a tela de sucesso
   */
  const renderSuccessScreen = () => (
    <Box
      sx={{ py: isMobile ? 3 : 6, textAlign: "center" }}
      key="success-screen"
    >
      <CheckCircleIcon
        sx={{
          fontSize: isMobile ? 70 : 100,
          color: "#00A884",
          mb: isMobile ? 2 : 3,
          animation: "scaleIn 0.5s ease-in-out",
          "@keyframes scaleIn": {
            "0%": { transform: "scale(0)", opacity: 0 },
            "50%": { transform: "scale(1.1)" },
            "100%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />

      <Typography
        variant={isMobile ? "h5" : "h4"}
        gutterBottom
        sx={{ fontWeight: 600, color: "#00A884", mb: isMobile ? 1 : 2 }}
      >
        Cadastro Realizado com Sucesso!
      </Typography>

      <Typography
        variant={isMobile ? "body1" : "h6"}
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        Bem-vindo ao Fluxmed
      </Typography>

      <Typography
        variant={isMobile ? "body2" : "body1"}
        color="text.secondary"
        sx={{ mb: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}
      >
        {watchedIsPractitioner
          ? `Seu cadastro foi concluído com sucesso! ${
              currentFeatures.defaultApp
                ? "Realize seu cadastro como médico para começar a atender pacientes."
                : "Realize seu cadastro como veterinário para começar a atender pacientes."
            }`
          : "Seu cadastro foi concluído com sucesso! Agora você já pode acessar nossa plataforma."}
      </Typography>

      <Paper
        sx={{
          p: isMobile ? 2 : 3,
          mb: isMobile ? 2 : 4,
          bgcolor: watchedIsPractitioner
            ? "rgba(0, 168, 132, 0.05)"
            : "grey.50",
          border: watchedIsPractitioner ? "2px solid #00A884" : "none",
          maxWidth: 500,
          mx: "auto",
        }}
      >
        <Typography
          variant={isMobile ? "body2" : "subtitle1"}
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Próximos Passos:
        </Typography>
        <Box
          component="ul"
          sx={{ textAlign: "left", pl: isMobile ? 2 : 3, m: 0 }}
        >
          {watchedIsPractitioner && (
            <Typography
              variant={isMobile ? "caption" : "body2"}
              component="li"
              color="#00A884"
              sx={{
                mb: 0.5,
                fontWeight: 600,
                "&::marker": { color: "#00A884" },
              }}
            >
              {currentFeatures.defaultApp
                ? "Complete seu registro médico para começar a atender"
                : "Complete seu registro veterinário para começar a atender"}
            </Typography>
          )}
          <Typography
            variant={isMobile ? "caption" : "body2"}
            component="li"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Faça login usando seu CPF e senha cadastrados
          </Typography>
          <Typography
            variant={isMobile ? "caption" : "body2"}
            component="li"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Complete seu perfil com informações adicionais
          </Typography>
          {!watchedIsPractitioner && (
            <>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                component="li"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Agende consultas e acompanhe sua saúde
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                component="li"
                color="text.secondary"
              >
                Acesse seu histórico médico a qualquer momento
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Card promocional para profissionais (sempre visível) */}
      {!watchedIsPractitioner && (
        <Paper
          elevation={4}
          sx={{
            p: isMobile ? 2.5 : 3,
            mb: isMobile ? 3 : 4,
            maxWidth: 500,
            mx: "auto",
            background:
              "linear-gradient(135deg, rgba(0, 168, 132, 0.1) 0%, rgba(0, 140, 110, 0.05) 100%)",
            border: "2px solid #00A884",
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #00A884 0%, #008c6e 100%)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            {currentFeatures.defaultApp ? (
              <HealingIcon
                sx={{
                  fontSize: isMobile ? 40 : 50,
                  color: "#00A884",
                  mr: 1.5,
                }}
              />
            ) : (
              <PetsIcon
                sx={{
                  fontSize: isMobile ? 40 : 50,
                  color: "#00A884",
                  mr: 1.5,
                }}
              />
            )}
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{
                fontWeight: 700,
                color: "#00A884",
                textAlign: "center",
              }}
            >
              {currentFeatures.defaultApp
                ? "Você é Médico(a)?"
                : "Você é Médico(a) Veterinário(a)?"}
            </Typography>
          </Box>

          <Typography
            variant={isMobile ? "body2" : "body1"}
            color="text.primary"
            sx={{
              mb: 2,
              textAlign: "center",
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            {currentFeatures.defaultApp
              ? "Use nossa plataforma para gerenciar seus pacientes, agendar consultas e muito mais!"
              : "Use nossa plataforma para gerenciar seus pacientes, agendar consultas e muito mais!"}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <CheckCircleIcon
                sx={{ fontSize: 20, color: "#00A884", mt: 0.2 }}
              />
              <Typography variant={isMobile ? "caption" : "body2"}>
                Gerencie seus pacientes de forma digital
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <CheckCircleIcon
                sx={{ fontSize: 20, color: "#00A884", mt: 0.2 }}
              />
              <Typography variant={isMobile ? "caption" : "body2"}>
                Agende consultas e organize sua agenda
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <CheckCircleIcon
                sx={{ fontSize: 20, color: "#00A884", mt: 0.2 }}
              />
              <Typography variant={isMobile ? "caption" : "body2"}>
                Acesse prontuários e histórico completo
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            fullWidth
            startIcon={<AssignmentIndIcon />}
            onClick={() => {
              // TODO: Implementar navegação para registro profissional
              toast.info(
                "Funcionalidade de registro profissional em desenvolvimento.",
              );
            }}
            sx={{
              bgcolor: "#00A884",
              px: isMobile ? 2 : 4,
              py: isMobile ? 1.3 : 1.6,
              fontSize: isMobile ? "0.95rem" : "1.05rem",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(0, 168, 132, 0.3)",
              "&:hover": {
                bgcolor: "#008c6e",
                boxShadow: "0 6px 16px rgba(0, 168, 132, 0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            {currentFeatures.defaultApp
              ? "Quero me Registrar como Médico"
              : "Quero me Registrar como Veterinário"}
          </Button>
        </Paper>
      )}

      <Stack spacing={isMobile ? 1.5 : 2} sx={{ maxWidth: 500, mx: "auto" }}>
        {/* Card clicável para registro profissional (quando marcado como médico/veterinário) */}
        {watchedIsPractitioner ? (
          <>
            <Paper
              elevation={6}
              onClick={() => {
                window.open(`${endpoints.XDSB_domain}/register/#/`);
              }}
              sx={{
                p: isMobile ? 3 : 4,
                cursor: "pointer",
                background:
                  "linear-gradient(135deg, rgba(0, 168, 132, 0.15) 0%, rgba(0, 140, 110, 0.1) 100%)",
                border: "3px solid #00A884",
                borderRadius: 3,
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(0, 168, 132, 0.3)",
                  borderColor: "#008c6e",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "5px",
                  background:
                    "linear-gradient(90deg, #00A884 0%, #008c6e 100%)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  {currentFeatures.defaultApp ? (
                    <HealingIcon
                      sx={{
                        fontSize: isMobile ? 50 : 60,
                        color: "#00A884",
                        mr: 1.5,
                      }}
                    />
                  ) : (
                    <PetsIcon
                      sx={{
                        fontSize: isMobile ? 50 : 60,
                        color: "#00A884",
                        mr: 1.5,
                      }}
                    />
                  )}
                  <AssignmentIndIcon
                    sx={{
                      fontSize: isMobile ? 30 : 35,
                      color: "#00A884",
                    }}
                  />
                </Box>

                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{
                    fontWeight: 700,
                    color: "#00A884",
                    mb: 1,
                  }}
                >
                  {currentFeatures.defaultApp
                    ? "Complete seu Registro Médico"
                    : "Complete seu Registro Veterinário"}
                </Typography>

                <Typography
                  variant={isMobile ? "body2" : "body1"}
                  color="text.primary"
                  sx={{
                    mb: 2,
                    fontWeight: 500,
                    lineHeight: 1.6,
                  }}
                >
                  {currentFeatures.defaultApp
                    ? "Finalize seu cadastro como médico e comece a atender pacientes na plataforma!"
                    : "Finalize seu cadastro como veterinário e comece a atender pacientes na plataforma!"}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 2,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                    />
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Gerencie seus pacientes de forma digital e segura
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                    />
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Agende consultas e organize sua agenda profissional
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                  >
                    <CheckCircleIcon
                      sx={{ fontSize: 22, color: "#00A884", mt: 0.2 }}
                    />
                    <Typography variant={isMobile ? "caption" : "body2"}>
                      Acesse prontuários e histórico completo dos pacientes
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  size={isMobile ? "medium" : "large"}
                  fullWidth
                  startIcon={<AssignmentIndIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`${endpoints.XDSB_domain}/register/#/`);
                  }}
                  sx={{
                    bgcolor: "#00A884",
                    px: isMobile ? 2 : 4,
                    py: isMobile ? 1.4 : 1.8,
                    fontSize: isMobile ? "1rem" : "1.1rem",
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(0, 168, 132, 0.3)",
                    "&:hover": {
                      bgcolor: "#008c6e",
                      boxShadow: "0 6px 16px rgba(0, 168, 132, 0.4)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  {currentFeatures.defaultApp
                    ? "Continuar Registro Médico"
                    : "Continuar Registro Veterinário"}
                </Button>
              </Box>
            </Paper>

            {/* Botão simples para login */}
            <Button
              variant="text"
              size={isMobile ? "medium" : "large"}
              fullWidth
              startIcon={<LoginIcon />}
              onClick={handleGoToLogin}
              sx={{
                color: "text.secondary",
                py: isMobile ? 1 : 1.5,
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Ir para Login
            </Button>
          </>
        ) : (
          /* Botão principal para login (quando não é profissional) */
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            fullWidth
            startIcon={<LoginIcon />}
            onClick={handleGoToLogin}
            sx={{
              bgcolor: "#00A884",
              px: isMobile ? 2 : 4,
              py: isMobile ? 1.2 : 1.5,
              "&:hover": {
                bgcolor: "#008c6e",
              },
            }}
          >
            Ir para Login
          </Button>
        )}
      </Stack>
    </Box>
  );

  /**
   * Renderiza o conteúdo da etapa atual
   */
  const renderStepContent = () => {
    switch (activeStep) {
      case SignupSteps.PERSONAL_DATA:
        return renderPersonalData();

      case SignupSteps.PASSWORD_CREATION:
        return renderPasswordCreation();

      case SignupSteps.TERMS_OF_USE:
        return renderTermsOfUse();

      case SignupSteps.SUCCESS:
        return renderSuccessScreen();

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Container
        maxWidth="md"
        sx={{
          py: isMobile ? 2 : 4,
          px: isMobile ? 1.5 : 3,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Estilos para animações */}
        <style>{loadingDotsStyle}</style>
        {/* Dialog de Token */}
        {renderTokenDialog()}

        {/* Header com logo/título */}
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: isMobile ? "12px" : "16px",
            boxShadow: 3,
            py: isMobile ? 1.5 : 2,
            mb: isMobile ? 1.5 : 2,
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: isMobile ? "50px" : "80px",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src={logo}
              alt="Fluxmed Logo"
              style={
                currentFeatures.defaultApp
                  ? {
                      height: isMobile ? "40px" : "70px",
                      width: "auto",
                      objectFit: "contain",
                      margin: "0 auto",
                    }
                  : {
                      height: isMobile ? "180px" : "220px",
                      width: "auto",
                      objectFit: "contain",
                      margin: "0 auto",
                    }
              }
            />
          </Box>
        </Stack>

        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 4,
            borderRadius: isMobile ? "12px" : "16px",
          }}
        >
          {/* Stepper - Indicador visual das etapas (simplificado para 2 passos) */}
          {/* Não mostrar na tela de sucesso */}
          {activeStep !== SignupSteps.SUCCESS && (
            <Stepper
              activeStep={getVisualStepIndex(activeStep)}
              alternativeLabel
              orientation="horizontal"
              sx={{
                mb: isMobile ? 2 : 4,
                // Remove a linha de conexão entre os passos
                "& .MuiStepConnector-root": {
                  display: "none",
                },
                // Ajustes para mobile
                "& .MuiStepLabel-root": {
                  flexDirection: "column",
                  alignItems: "center",
                },
                "& .MuiStepLabel-labelContainer": {
                  mt: 0.5,
                },
              }}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={(props) =>
                      StepIconComponent({ ...props, icon: index + 1 })
                    }
                  >
                    <Typography
                      variant={isMobile ? "body2" : "h6"}
                      sx={{ fontWeight: 500, textAlign: "center" }}
                    >
                      {step.label}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Conteúdo da etapa atual */}
          <Box sx={{ mt: isMobile ? 1 : 3 }} key={`step-${activeStep}`}>
            {renderStepContent()}
          </Box>

          {/* Botões de navegação (não mostrar na tela de sucesso) */}
          {activeStep !== SignupSteps.SUCCESS && (
            <Stack
              direction={isMobile ? "column-reverse" : "row"}
              spacing={isMobile ? 1.5 : 2}
              sx={{ mt: isMobile ? 3 : 4 }}
              justifyContent="space-between"
            >
              <Button
                variant="outlined"
                fullWidth={isMobile}
                startIcon={
                  activeStep === SignupSteps.PERSONAL_DATA ? (
                    <LoginIcon />
                  ) : undefined
                }
                onClick={() => {
                  if (activeStep === SignupSteps.PERSONAL_DATA) {
                    navigate("/signin");
                    return;
                  }
                  handleBack();
                }}
                disabled={loading}
                sx={{
                  borderColor: "#00A884",
                  color: "#00A884",
                  py: isMobile ? 1.2 : 1,
                  "&:hover": {
                    borderColor: "#008c6e",
                    bgcolor: "rgba(0, 168, 132, 0.04)",
                  },
                }}
              >
                {activeStep === SignupSteps.PERSONAL_DATA
                  ? "Já tenho uma conta"
                  : "Voltar"}
              </Button>

              {/* Botão condicional baseado no passo atual */}
              {activeStep === SignupSteps.PERSONAL_DATA ? (
                <Button
                  variant="contained"
                  fullWidth={isMobile}
                  onClick={() => {
                    // Verificar validade e mostrar feedback visual
                    if (!isStepValid()) {
                      toast.warn(
                        "Preencha todos os campos obrigatórios corretamente",
                      );
                    } else {
                      handleSendToken();
                    }
                  }}
                  disabled={loading}
                  sx={{
                    bgcolor: "#00A884",
                    py: isMobile ? 1.2 : 1,
                    "&:hover": {
                      bgcolor: "#008c6e",
                    },
                  }}
                >
                  {loading ? "Enviando..." : "Continuar"}
                </Button>
              ) : activeStep === SignupSteps.PASSWORD_CREATION ? (
                <Button
                  variant="contained"
                  fullWidth={isMobile}
                  onClick={handleSubmit(handleConfirmPassword)}
                  disabled={loading || !isStepValid()}
                  sx={{
                    bgcolor: "#00A884",
                    py: isMobile ? 1.2 : 1,
                    "&:hover": {
                      bgcolor: "#008c6e",
                    },
                  }}
                >
                  {loading ? "Finalizando..." : "Confirmar e Continuar"}
                </Button>
              ) : activeStep === SignupSteps.TERMS_OF_USE ? (
                <Button
                  variant="contained"
                  fullWidth={isMobile}
                  onClick={handleSubmit(handleCompleteRegistration)}
                  disabled={loading || !watchedTermsAccepted}
                  sx={{
                    bgcolor: "#00A884",
                    py: isMobile ? 1.2 : 1,
                    "&:hover": {
                      bgcolor: "#008c6e",
                    },
                  }}
                >
                  {loading ? "Finalizando..." : "Aceitar e Finalizar"}
                </Button>
              ) : null}
            </Stack>
          )}

          {/* Indicador de progresso textual (não mostrar na tela de sucesso) */}
          {activeStep !== SignupSteps.SUCCESS && (
            <Box sx={{ mt: isMobile ? 2 : 3, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Etapa {getVisualStepIndex(activeStep) + 1} de {steps.length}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupPatient;
